// Defaults & Workout rebuild — presentational 6-stage rollup of the
// 21-state DefaultCase machine (the FE twin of the backend
// DwM1DefaultWorkoutStatusRenderer.StageFor). The state machine itself is
// untouched: stages exist only for the banner chip ladder and the
// stage-action rail.

export type CaseStage =
  | "triggered"
  | "notice"
  | "cure"
  | "workout"
  | "resolution"
  | "closed";

export const CASE_STAGES: { key: CaseStage; label: string }[] = [
  { key: "triggered", label: "Triggered" },
  { key: "notice", label: "Notice" },
  { key: "cure", label: "Cure" },
  { key: "workout", label: "Workout" },
  { key: "resolution", label: "Resolution" },
  { key: "closed", label: "Closed" },
];

const STAGE_BY_STATUS: Record<string, CaseStage> = {
  "default-declared": "triggered",
  "notice-of-default-sent": "notice",
  "cure-period-active": "cure",
  "cure-period-expired": "cure",
  "forbearance-executed": "workout",
  "workout-in-progress": "workout",
  "acceleration-declared": "workout",
  "demand-letter-sent": "workout",
  "replevin-initiated": "workout",
  "judicial-foreclosure-filed": "workout",
  "receiver-appointed": "workout",
  "article-9-disposition-initiated": "workout",
  "deed-in-lieu-accepted": "workout",
  "bill-of-sale-in-lieu-accepted": "workout",
  "guaranty-enforcement-initiated": "workout",
  "recovery-applied": "resolution",
  "charged-off": "resolution",
  "deficiency-claim-pursued": "resolution",
  "resolved-recovered": "resolution",
  "cured-restored": "resolution",
  "closed-final": "closed",
};

export function stageForStatus(status: string): CaseStage {
  return STAGE_BY_STATUS[status] ?? "triggered";
}

export function stageIndex(stage: CaseStage): number {
  return CASE_STAGES.findIndex((s) => s.key === stage);
}

export function formatCaseStatus(status: string): string {
  return status.replace(/-/g, " ");
}

// ----- stage-action promotion ----------------------------------------------

/** Every action the workspace can launch. Promotion is emphasis, not
 *  removal — everything stays reachable via the "All actions" overflow. */
export type CaseAction =
  | "add-event"
  | "add-note"
  | "propose-plan"
  | "propose-forbearance"
  | "activate-daca"
  | "initiate-legal"
  | "engage-enforcer"
  | "record-cost"
  | "record-recovery"
  | "close-case";

export const CASE_ACTION_LABELS: Record<CaseAction, string> = {
  "add-event": "Record event",
  "add-note": "Add note",
  "propose-plan": "Propose remediation plan",
  "propose-forbearance": "Propose forbearance (E2)",
  "activate-daca": "Activate DACA (E2)",
  "initiate-legal": "Initiate legal action",
  "engage-enforcer": "Engage enforcer",
  "record-cost": "Record cost",
  "record-recovery": "Record recovery (E1)",
  "close-case": "Close case (E1)",
};

/** The actions promoted onto the rail for each stage (five-second rule:
 *  what matters NOW leads). */
const PROMOTED_BY_STAGE: Record<CaseStage, CaseAction[]> = {
  triggered: ["add-event", "propose-plan", "add-note"],
  notice: ["add-event", "propose-plan", "add-note"],
  cure: ["add-event", "propose-plan", "propose-forbearance"],
  workout: [
    "propose-forbearance",
    "activate-daca",
    "initiate-legal",
    "engage-enforcer",
    "record-cost",
    "record-recovery",
  ],
  resolution: ["record-recovery", "record-cost", "close-case"],
  // closed-final is terminal — the rail renders nothing (no writes).
  closed: [],
};

export function promotedActionsFor(stage: CaseStage): CaseAction[] {
  return PROMOTED_BY_STAGE[stage];
}

export const ALL_CASE_ACTIONS: CaseAction[] = [
  "add-event",
  "add-note",
  "propose-plan",
  "propose-forbearance",
  "activate-daca",
  "initiate-legal",
  "engage-enforcer",
  "record-cost",
  "record-recovery",
  "close-case",
];

// ----- per-action gating (unchanged semantics from the card era) -------------

export interface ActionGates {
  hasPermission: (permission: string) => boolean;
  hasRoleCode: (roleCode: string) => boolean;
}

/** Mirrors the backend endpoint gates exactly (Defaults.Manage for the
 *  case-core actions; the specialist permissions for DACA / legal /
 *  ledgers / engagements; Default.WriteOff for closure). Role fallbacks
 *  match the pre-rebuild cards so freshly-granted permissions and stale
 *  sessions behave the same. */
export function canPerformAction(action: CaseAction, auth: ActionGates): boolean {
  const admin = auth.hasRoleCode("ADMIN");
  const manage = auth.hasPermission("Defaults.Manage") || admin;
  switch (action) {
    case "add-event":
    case "add-note":
    case "propose-plan":
    case "propose-forbearance":
      return manage;
    case "activate-daca":
      return auth.hasPermission("Default.DACAActivate") || auth.hasRoleCode("E2") || admin;
    case "initiate-legal":
      return auth.hasPermission("Default.LegalAction") || admin;
    case "engage-enforcer":
      return auth.hasPermission("Enforcer.Engage") || admin;
    case "record-cost":
      return auth.hasPermission("Workout.RecordCost") || admin;
    case "record-recovery":
      return (
        auth.hasPermission("Workout.ApproveRecovery") || auth.hasRoleCode("E1") || admin
      );
    case "close-case":
      return (
        auth.hasPermission("Default.WriteOff") || auth.hasRoleCode("E1") || admin
      );
  }
}
